import React from 'react';
import { styled } from '../../stitches.config';
import { h1, bodyLarge } from "../commonStyles/Fonts";

import LinkIcon from "../assets/icons/author-link-icon.svg";

import useMediaQuery from '../../helpers/useMediaQuery';

import Media from "../../components/common/Media";
import SlateRenderer from "../../components/Article/Parts/Slate/SlateRenderer";
import TextWrapper from './TextWrapper';
import TwitterIcon from "../assets/icons/social/twitter-white.svg";
import LinkedinIcon from "../assets/icons/social/linkedin-white.svg";
import InstagramIcon from "../assets/icons/social/instagram-white.svg";
import TiktokIcon from "../assets/icons/social/tiktok-white.svg";

interface IPageCover {
    title: React.ReactNode;
    description: any;
    image?: string;
    media?: string;
    isDescriptionWyswig?: boolean;
    extraMargin?: boolean;
    beforeContent?: React.ReactNode;
    hideMobileImage?: boolean;
    itemProp?: boolean;
    socials?: {
        twitter?: string;
        linkedin?: string;
        instagram?: string;
        tiktok?: string;
    };
};

const SOCIAL_CONFIG = {
    twitter: {
        label: "Twitter",
        icon: TwitterIcon,
    },
    linkedin: {
        label: "LinkedIn",
        icon: LinkedinIcon,
    },
    instagram: {
        label: "Instagram",
        icon: InstagramIcon,
    },
    tiktok: {
        label: "TikTok",
        icon: TiktokIcon,
    },
};

const PageCover: React.FC<IPageCover> = ({ title, description, image, media, isDescriptionWyswig, extraMargin, beforeContent, hideMobileImage, socials, itemProp }) => {

    const isDesktop = useMediaQuery('bp4');

    return (
        <CoverWrapper extraMargin={extraMargin}>
            <Container>
                {beforeContent}
                <ContentWrapper>
                    <PageDetails>
                        <PageTitle className={h1()} itemProp={itemProp ? "name" : undefined}>
                            {title}
                        </PageTitle>
                        {socials && Object.entries(socials).some(([, value]) => value) && (
                            <Socials>
                                {Object.entries(socials).map(([key, url]) => {
                                    if (!url) return null;

                                    const social = SOCIAL_CONFIG[key as keyof typeof SOCIAL_CONFIG];

                                    return (
                                        <SocialLink
                                            key={key}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={social.label}
                                            isLinkedin={key === "linkedin"}
                                            itemProp={itemProp ? "sameAs" : undefined}
                                        >
                                            <img src={social.icon} alt={social.label} />
                                        </SocialLink>
                                    );
                                })}
                            </Socials>
                        )}
                        <TextWrapper>
                            {isDescriptionWyswig ? (
                                <p className={bodyLarge()}>
                                    {description && <SlateRenderer parts={description} />}
                                </p>
                            ) : (
                                <p className={bodyLarge()} dangerouslySetInnerHTML={{ __html: description }} />
                            )}
                        </TextWrapper>
                    </PageDetails>
                    {image && (
                        <PageImage hideMobileImage={hideMobileImage}>
                            <Media
                                media={media}
                                src={image}
                                preserveRatio={false}
                                width={isDesktop ? 300 : 328}
                                height={isDesktop ? 300 : 328}
                                alt={title}
                                disablePreload={true}
                                aspectRatio={1}
                            />
                        </PageImage>
                    )}
                </ContentWrapper>
            </Container>
        </CoverWrapper>
    );

};

export default PageCover;

const CoverWrapper = styled("div", {
    backgroundColor: "$grayscale100Fixed",
    width: "100%",
    padding: "32px 0",
    "@bp4": {
        padding: "90px 0",
    },
    variants: {
        extraMargin: {
            true: {
                marginTop: "33px",
                '@bp4': {
                    marginTop: "84px",
                    marginBottom: '80px'
                }
            },
            false: {}
        }
    }
});

const Socials = styled("div", {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
});

const SocialLink = styled("a", {
    width: "20px",
    height: "20px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    "@bp4": {
        width: "24px",
        height: "24px",
    },

    variants: {
        isLinkedin: {
            true: {
                width: "22px",
                height: "22px",

                "@bp4": {
                    width: "26px",
                    height: "26px",
                },
            },
        },
    },

    img: {
        width: "100%",
        height: "100%",
    },

    "&:hover": {
        opacity: 0.8,
    },
});

const Container = styled("div", {
    margin: "0 auto",
    padding: "0 16px",
    "@bp3": {
        width: "960px",
    },
    "@bp4": {
        padding: "0 36px",
        width: "1160px",
    },
    "@bp6": {
        width: "1344px",
    },
});

const ContentWrapper = styled("div", {
    display: "flex",
    flexDirection: "column-reverse",
    "@bp4": {
        display: "grid",
        gridTemplateColumns: "1fr 300px",
        alignItems: "top",
        marginTop: "33px",
    },
});

const PageDetails = styled("div", {
    color: "$grayscale0Fixed !important",
    maxWidth: "760px",
    "& .author-copy": {
        marginLeft: "8px",
        width: "17px",
        height: "17px",
        backgroundImage: `url("${LinkIcon}")`,
        backgroundSize: "cover",
        backgroundColor: "$grayscale100Fixed",
        borderWidth: "0",
    },
    "& .author-message": {
        color: "$grayscale75",
        display: "inline-block",
    },
    "& p": {
        "& div": {
            color: "$grayscale0Fixed !important",
        },
    },
});

const PageTitle = styled("h1", {
    margin: "24px 0",
    lineHeight: 1,
});

const PageImage = styled("div", {
    marginTop: "12px",
    position: "relative",
    width: "328px",
    height: "328px",
    "@bp4": {
        marginTop: "0px",
        marginLeft: "auto",
        height: "300px",
        width: "300px",
    },
    div: {
        background: "transparent",
    },

    variants: {
        hideMobileImage: {
            true: {
                display: "none",
                "@bp4": {
                    display: "block",
                }
            }
        }
    }
});